import cloneDeep from "lodash/cloneDeep";
import range from "lodash/range";
import PropTypes from "prop-types";
import React from "react";
import { sortNumericAsc } from "util/sort-by-prop";

import { OperationScheduleCell } from "./operation-schedule-cell";

const DAYS = {
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
  6: "Saturday",
  7: "Sunday",
};

const NUM_HOURS = [...Array(24).keys()];

class OperationScheduleForm extends React.Component {
  static get propTypes() {
    return {
      schedule: PropTypes.string,
      readOnly: PropTypes.bool.isRequired,
      onChange: PropTypes.func.isRequired,
    };
  }

  constructor(props) {
    super(props);
    this.state = {
      activeDay: null,
      dayOperateTime: {},
      startHour: null,
      shouldSelect: null,
      hoverHour: null,
    };

    this._onUpdateDay = this._onUpdateDay.bind(this);
    this._cancelSelectOnMouseup = this._cancelSelectOnMouseup.bind(this);
    this._formatDeviceOperateTime = this._formatDeviceOperateTime.bind(this);
  }

  componentDidMount() {
    const dayOperateTime = this._parseOperateTime(this.props.schedule);
    this.setState({ dayOperateTime });
  }

  componentDidUpdate(prevProps) {
    const schedule = this.props.schedule;
    if (schedule !== prevProps.schedule) {
      const dayOperateTime = this._parseOperateTime(schedule);
      this.setState({ dayOperateTime });
    }
  }

  _cancelSelectOnMouseup(e) {
    if (this.activeDayRef && !this.activeDayRef.contains(e.target)) {
      this.setState(
        {
          activeDay: null,
          startHour: null,
          shouldSelect: null,
          hoverHour: null,
        },
        () => {
          window.removeEventListener("mouseup", this._cancelSelectOnMouseup);
        },
      );
    }
  }

  _formatDeviceOperateTime(day, hours, shouldSelect, dayOperateTime) {
    dayOperateTime = cloneDeep(dayOperateTime);
    let activeHours = [...NUM_HOURS];

    if (day in dayOperateTime) {
      activeHours = dayOperateTime[day].activeHours;
    } else {
      dayOperateTime[day] = {};
    }

    if (shouldSelect) {
      const _hours = hours
        .filter((h) => {
          // Convert to string then back to int for cases where h is 0
          return activeHours.indexOf(h) === -1 ? String(h) : undefined;
        })
        .map((h) => parseInt(h, 10));
      activeHours = activeHours.concat(_hours);
    } else {
      activeHours = activeHours.filter((ah) => hours.indexOf(ah) === -1);
    }

    activeHours.sort(sortNumericAsc);

    dayOperateTime[day].activeHours = activeHours;

    return this._stringifyOperateParams(dayOperateTime);
  }

  _stringifyOperateParams(dayOperateTime) {
    return Object.entries(dayOperateTime)
      .map(([day, p]) => `${day}-${p.activeHours.join("-")}`)
      .join(",");
  }

  _onStartSelect(day, hour, shouldSelect) {
    if (this.props.readOnly) {
      return;
    }

    this.setState(
      {
        activeDay: day,
        startHour: hour,
        hoverHour: hour,
        shouldSelect: shouldSelect,
      },
      () => {
        window.addEventListener("mouseup", this._cancelSelectOnMouseup);
      },
    );
  }

  _onHoverHour(hour) {
    if (!this.state.activeDay) {
      return;
    }
    this.setState({ hoverHour: hour });
  }

  _onUpdateDay(day, hour) {
    const { dayOperateTime, startHour, shouldSelect } = this.state;
    if (this.props.readOnly) {
      return;
    }

    this.setState(
      {
        activeDay: null,
        startHour: null,
        shouldSelect: null,
        hoverHour: null,
      },
      () => {
        window.removeEventListener("mouseup", this._cancelSelectOnMouseup);

        const deviceOperateTime = this._formatDeviceOperateTime(
          day,
          range(Math.min(startHour, hour), Math.max(startHour, hour) + 1),
          shouldSelect,
          dayOperateTime,
        );
        this.props.onChange(deviceOperateTime);
      },
    );
  }

  _parseOperateTime(operateTime) {
    if (!operateTime) {
      return {};
    }

    /** The operateTime format is like the following:
     *  1-8-9-10-12-13-14 means, day: 1, hours: [8, 9, 10, 12, 13, 14]
     */
    const weekParams = operateTime.split(",");
    const dayOperateTime = weekParams.reduce((acc, dayParam) => {
      let [day, ...operateHours] = dayParam.split("-");
      day = parseInt(day, 10);
      operateHours = operateHours.map((h) => parseInt(h, 10));
      const activeHours = operateHours.filter((h) => h >= 0 && h <= 23);
      acc[day] = { activeHours };
      return acc;
    }, {});

    return dayOperateTime;
  }

  _isSelectedHour(day, hour) {
    if (day.activeHours) {
      return day.activeHours.indexOf(hour) >= 0;
    }
    return true;
  }

  _getHoverStatus(day, hour) {
    const { activeDay, startHour, hoverHour, shouldSelect } = this.state;
    if (day !== activeDay) {
      return;
    }

    if (
      (startHour <= hour && hour <= hoverHour) ||
      (startHour >= hour && hour >= hoverHour)
    ) {
      return shouldSelect ? "selecting" : "deselecting";
    }
    return false;
  }

  _renderDay(dayNum, day) {
    const { activeDay } = this.state;
    return (
      <div
        key={dayNum}
        className="op-sched--day"
        ref={(node) => {
          if (dayNum === activeDay) {
            this.activeDayRef = node;
          }
        }}
      >
        <div className="op-sched--title">{DAYS[dayNum]}</div>
        {NUM_HOURS.map((h) => {
          const isSelected = this._isSelectedHour(day, h);
          const hoverStatus = this._getHoverStatus(dayNum, h);
          return (
            <OperationScheduleCell
              key={`${dayNum}--${h}`}
              hour={h}
              day={dayNum}
              onMouseDown={() => this._onStartSelect(dayNum, h, !isSelected)}
              onMouseUp={() => this._onUpdateDay(dayNum, h)}
              onHover={() => this._onHoverHour(h)}
              isSelected={isSelected}
              hoverStatus={hoverStatus}
              readOnly={this.props.readOnly}
              disabled={activeDay && dayNum !== activeDay}
            />
          );
        })}
      </div>
    );
  }

  render() {
    const { dayOperateTime } = this.state;
    return (
      <div className="op-sched">
        {Object.keys(DAYS).map((dayStr) => {
          const dayNum = parseInt(dayStr, 10);
          return this._renderDay(
            dayNum,
            dayOperateTime[dayNum] || { start: 0, end: 24 },
          );
        })}
      </div>
    );
  }
}

export { OperationScheduleForm };
